/**
 * Разговор оболочки с сервером (тикет T18b).
 *
 * Раскладка и Настройки ходят на сервер через свои двери в ядре клиента — их здесь нет.
 * Сюда попало остальное: вход, Каналы, Сообщения, Задачи. Правило то же самое: ключ
 * сессии оболочке неизвестен, он лежит в закрытой куке и уходит сам.
 *
 * Объяснение беды берём то, что прислал сервер: оно уже на языке смотрящего и написано
 * про конкретный случай. Свой текст поверх серверного стёр бы именно то, ради чего его
 * писали, — человек увидел бы «что-то пошло не так» вместо «срок раньше сегодняшнего дня».
 * Свои слова остаются только там, где сервер не сказал ничего: он лёг или до него не дошли.
 */
import { translatorFor } from '../i18n/dictionary.js';
import type { Translator } from '../i18n/dictionary.js';
import type { Language } from '@core/settings/appearance.js';

/** Беда, о которой сервер сказал словами. */
export class ApiError extends Error {
  constructor(
    message: string,
    /** Код беды — для оболочки, а не для человека. */
    readonly code: string,
    /** Что ответил сервер: 401 отправляет ко входу, остальное показывается словами. */
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Member {
  readonly id: string;
  readonly displayName: string;
  readonly role: string;
}

export interface Channel {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  /** Номер последнего Сообщения внутри Канала — от него считается непрочитанное. */
  readonly lastSeq?: number;
}

/**
 * Что за Сообщение лежит на этом месте ленты. `removed` — надгробие: текст убран, но
 * место в разговоре осталось за ним, иначе соседние реплики теряют смысл.
 */
export type MessageKind = 'user' | 'system' | 'removed';

export interface Message {
  readonly id: string;
  readonly seq: number;
  /** Номер внутри Канала: он и задаёт порядок ленты, сквозной `seq` — только для сверки. */
  readonly channelSeq: number;
  readonly channelId: string;
  readonly authorId: string;
  readonly body: string;
  readonly kind: MessageKind;
  /**
   * Сколько раз Сообщение правили. Это же число человек отправляет обратно при правке —
   * «я правлю то, что видел». Не сошлось — сервер отказывает, а не затирает чужую правку.
   */
  readonly revision: number;
  readonly createdAt: string;
  readonly editedAt: string | null;
  readonly editedBy: string | null;
  readonly deletedAt: string | null;
  readonly deletedBy: string | null;
}

/** Прежняя редакция Сообщения: что было написано до правки и кто её внёс. */
export interface MessageRevision {
  readonly id: string;
  readonly messageId: string;
  readonly revision: number;
  readonly body: string;
  readonly editedBy: string;
  readonly editedAt: string;
}

/** Сколько Сообщений в Канале человек ещё не видел. */
export interface ChannelUnread {
  readonly channelId: string;
  readonly unread: number;
}

export interface Task {
  readonly id: string;
  readonly title: string;
  readonly assigneeId: string;
  readonly dueAt: string;
  readonly state: string;
  /** Цвет полосы срочности, посчитанный сервером по порогам фирмы (§ 7). */
  readonly urgency: string;
}

export interface Me {
  readonly memberId: string;
  readonly role: string;
}

/** Роли, которые Администратор раздаёт при Приглашении. */
export const INVITE_ROLES = ['staff', 'head', 'guest', 'admin'] as const;
export type InviteRole = (typeof INVITE_ROLES)[number];

/**
 * Выпущенное Приглашение. Ключ виден один раз — сервер хранит только его отпечаток,
 * поэтому переспросить ссылку нельзя, можно лишь выпустить новую.
 */
export interface Invitation {
  /** Путь вида `/фирма/join/<ключ>` — оболочка достраивает его до полного адреса. */
  readonly path: string;
  readonly expiresAt: string;
}

/** Кто ходит по сети. Отдельным полем — чтобы оболочка проверялась без сети. */
export type Fetcher = (url: string, init?: RequestInit) => Promise<Response>;

const parse = async (response: Response, say: Translator): Promise<unknown> => {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (response.ok) return payload;

  const said = payload as { error?: unknown; message?: unknown } | null;
  const message = typeof said?.message === 'string' && said.message.trim() ? said.message : null;
  const code = typeof said?.error === 'string' ? said.error : 'unknown';
  // Когда сервер лежит, его собственных слов нет — и наши приходится брать из словаря.
  // Скажи мы их по-русски, читающий на иврите увидел бы русскую строку ровно в тот момент,
  // когда меньше всего понимает, что происходит.
  if (response.status >= 500) throw new ApiError(say('common.serverDown'), code, response.status);
  throw new ApiError(message ?? say('common.serverRefused'), code, response.status);
};

export interface ApiOptions {
  readonly baseUrl?: string;
  readonly language?: Language;
  readonly fetch?: Fetcher;
}

const list = (raw: unknown): unknown[] => (Array.isArray(raw) ? raw : []);

export interface Api {
  readonly spaceSlug: string;
  signIn(input: { email: string; password: string }): Promise<void>;
  signOut(): Promise<void>;
  me(): Promise<Me>;
  channels(): Promise<Channel[]>;
  members(): Promise<Member[]>;
  messages(channelId: string): Promise<Message[]>;
  postMessage(channelId: string, text: string): Promise<Message>;
  /**
   * Отметить, что дочитано до этого номера. Сервер двигает отметку только вперёд, поэтому
   * звать это можно смело: пролистав ленту вверх, человек не вернёт себе непрочитанные.
   */
  markRead(channelId: string, seq: number): Promise<number>;
  /** Непрочитанное по всем Каналам разом — одним запросом, а не по одному на Канал. */
  unread(): Promise<ChannelUnread[]>;
  /**
   * Поправить своё Сообщение. `revision` — та редакция, которую человек видел на экране:
   * успели поправить с другого устройства — сервер откажет словами, а не затрёт молча.
   */
  editMessage(messageId: string, text: string, revision: number): Promise<Message>;
  /** Убрать Сообщение. Возвращается надгробие — его и рисуем на прежнем месте. */
  removeMessage(messageId: string): Promise<Message>;
  /** Стереть насовсем, вместе с историей правок. Только Администратору. */
  purgeMessage(messageId: string): Promise<void>;
  /** Прежние редакции Сообщения, свежая первой. */
  messageRevisions(messageId: string): Promise<MessageRevision[]>;
  tasks(): Promise<Task[]>;
  createChannel(input: { name: string; kind: 'open' | 'private' }): Promise<Channel>;
  /**
   * Позвать человека в фирму. Названная Роль допускает почту в Список разрешённых, и там
   * же, одним разговором с сервером, выпускается Приглашение: Приглашение на почту вне
   * Списка сервер не выпускает, а два отдельных запроса рвались бы посередине.
   */
  invite(input: { email: string; role: InviteRole }): Promise<Invitation>;
  /** Принять Приглашение: имя и пароль человек задаёт себе сам, Администратор их не знает. */
  acceptInvitation(input: { token: string; displayName: string; password: string }): Promise<void>;
}

export function createApi(spaceSlug: string, options: ApiOptions = {}): Api {
  const go = options.fetch ?? ((url, init) => fetch(url, init));
  // Язык ещё неизвестен (до входа Настройки спрашивать не у кого) — говорим на языке фирмы.
  const say = translatorFor(options.language ?? 'ru');
  const base = `${options.baseUrl ?? ''}/spaces/${encodeURIComponent(spaceSlug)}`;
  const withLanguage = (url: string): string =>
    options.language ? `${url}${url.includes('?') ? '&' : '?'}lang=${encodeURIComponent(options.language)}` : url;

  const request = async (path: string, init?: RequestInit): Promise<unknown> => {
    let response: Response;
    try {
      response = await go(withLanguage(`${base}${path}`), { credentials: 'include', ...init });
    } catch {
      throw new ApiError(say('common.offline'), 'offline', 0);
    }
    return parse(response, say);
  };

  const send = (path: string, method: string, payload: unknown): Promise<unknown> =>
    request(path, {
      method,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });

  return {
    spaceSlug,

    async signIn(input) {
      await send('/login', 'POST', input);
    },

    async signOut() {
      await send('/logout', 'POST', {});
    },

    async me() {
      const answer = (await request('/me')) as Partial<Me> | null;
      if (typeof answer?.memberId !== 'string' || typeof answer.role !== 'string') {
        throw new ApiError(say('common.badAnswer'), 'bad_response', 0);
      }
      return { memberId: answer.memberId, role: answer.role };
    },

    async channels() {
      return list(await request('/channels')) as Channel[];
    },

    async members() {
      return list(await request('/members')) as Member[];
    },

    async messages(channelId) {
      return list(await request(`/channels/${encodeURIComponent(channelId)}/messages`)) as Message[];
    },

    async postMessage(channelId, text) {
      return (await send(`/channels/${encodeURIComponent(channelId)}/messages`, 'POST', {
        body: text,
      })) as Message;
    },

    async markRead(channelId, seq) {
      const answer = (await send(`/channels/${encodeURIComponent(channelId)}/read`, 'POST', {
        seq,
      })) as { unread?: unknown };
      return typeof answer?.unread === 'number' ? answer.unread : 0;
    },

    async unread() {
      return list(await request('/unread')) as ChannelUnread[];
    },

    async editMessage(messageId, text, revision) {
      return (await send(`/messages/${encodeURIComponent(messageId)}`, 'PATCH', {
        body: text,
        revision,
      })) as Message;
    },

    async removeMessage(messageId) {
      return (await send(`/messages/${encodeURIComponent(messageId)}`, 'DELETE', {})) as Message;
    },

    async purgeMessage(messageId) {
      await send(`/messages/${encodeURIComponent(messageId)}/purge`, 'DELETE', {});
    },

    async messageRevisions(messageId) {
      return list(
        await request(`/messages/${encodeURIComponent(messageId)}/revisions`),
      ) as MessageRevision[];
    },

    async tasks() {
      const answer = (await request('/tasks')) as { tasks?: unknown };
      return list(answer?.tasks) as Task[];
    },

    async createChannel(input) {
      return (await send('/channels', 'POST', input)) as Channel;
    },

    async invite(input) {
      // Одним запросом, а не двумя: допуск почты и выпуск ссылки сервер делает вместе.
      // Разорви это надвое — и обрыв связи посередине оставил бы почту допущенной без
      // Приглашения, то есть вход открытым, при отказе на экране.
      const answer = (await send('/invitations', 'POST', input)) as Partial<Invitation> | null;
      if (typeof answer?.path !== 'string' || typeof answer.expiresAt !== 'string') {
        throw new ApiError(say('common.badAnswer'), 'bad_response', 0);
      }
      return { path: answer.path, expiresAt: answer.expiresAt };
    },

    async acceptInvitation(input) {
      await send('/invitations/accept', 'POST', input);
    },
  };
}
